import { forwardRef } from 'react';
import type { ComponentPropsWithoutRef, ElementRef, HTMLAttributes } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { cn } from '@/lib/cn';

/**
 * A side-anchored panel — same Radix primitive as `Dialog` (focus trap, scroll
 * lock, Esc, portal) but slides in from the right and is sized for dense detail
 * views (the admin user drawer). Compose exactly like `Dialog`:
 *
 * ```tsx
 * <Sheet open={open} onOpenChange={setOpen}>
 *   <SheetContent>
 *     <SheetHeader><SheetTitle>…</SheetTitle></SheetHeader>
 *     …body…
 *   </SheetContent>
 * </Sheet>
 * ```
 */
export const Sheet = DialogPrimitive.Root;
export const SheetTrigger = DialogPrimitive.Trigger;
export const SheetClose = DialogPrimitive.Close;

export const SheetContent = forwardRef<
  ElementRef<typeof DialogPrimitive.Content>,
  ComponentPropsWithoutRef<typeof DialogPrimitive.Content>
>(({ className, children, ...props }, ref) => (
  <DialogPrimitive.Portal>
    <DialogPrimitive.Overlay className="dialog-overlay fixed inset-0 z-50 bg-ink/40 backdrop-blur-sm" />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        'dialog-content fixed inset-y-0 right-0 z-50 flex w-[calc(100vw-2rem)] max-w-lg flex-col',
        'border-l-2 border-hairline bg-surface shadow-xl focus:outline-none',
        className,
      )}
      {...props}
    >
      <div className="flex-1 overflow-y-auto p-6">{children}</div>
      <DialogPrimitive.Close
        className="absolute right-4 top-4 inline-flex size-8 items-center justify-center rounded-full text-ink-faint transition-colors hover:bg-surface-sunken hover:text-ink focus-visible:outline-2 focus-visible:outline-ink"
        aria-label="Close"
      >
        <X className="size-4" strokeWidth={2} />
      </DialogPrimitive.Close>
    </DialogPrimitive.Content>
  </DialogPrimitive.Portal>
));
SheetContent.displayName = 'SheetContent';

export function SheetHeader({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('mb-4 flex flex-col gap-1.5 pr-8', className)} {...props} />;
}

export const SheetTitle = forwardRef<
  ElementRef<typeof DialogPrimitive.Title>,
  ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn('font-display text-xl font-semibold text-ink', className)}
    {...props}
  />
));
SheetTitle.displayName = 'SheetTitle';

export const SheetDescription = forwardRef<
  ElementRef<typeof DialogPrimitive.Description>,
  ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn('text-sm text-ink-soft', className)}
    {...props}
  />
));
SheetDescription.displayName = 'SheetDescription';
